import { useEffect, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  dcaScenariosAtom,
  selectedDcaScenarioAtom,
} from 'components/well-page-content/atoms';
import { defaultDcaScenario } from 'components/well-page-content';

/**
 * Auto-select a default DCA scenario on the Inventory Workspace page.
 *
 * The DCA scenario selector lives in the left sidebar (name + edit) and a right
 * panel (`DcaScenarioSelection`), both already wired on the page. But the fetch +
 * auto-select normally only run when that panel mounts — and the sidebar row (the
 * page's only entry point) is hidden until a scenario is selected, so nothing
 * ever picks one. This mirrors `DcaScenarioSelector`'s own on-mount fetch: it
 * loads the scenarios once and selects "pdp default" (or the first) into the
 * page-wide `selectedDcaScenarioAtom`, which surfaces the sidebar row and makes
 * the picker reachable.
 *
 * Runs once, and only when nothing is selected yet, so a scenario already chosen
 * (the atom is page-global) is left untouched.
 *
 * @return {void}
 */
export function useInitSelectedDcaScenario(): void {
  const [, fetchDcaScenarios] = useAtom(dcaScenariosAtom);
  const selectedDcaScenario = useAtomValue(selectedDcaScenarioAtom);
  const setSelectedDcaScenario = useSetAtom(selectedDcaScenarioAtom);
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    // Something is already selected (e.g. carried over from another page) — leave
    // it alone rather than refetching / clobbering the user's choice.
    if (selectedDcaScenario) return;
    hasRunRef.current = true;

    fetchDcaScenarios({
      url: '/queries/reservoir/dca_scenarios',
      type: 'POST',
      data: {
        query: {
          page: 1,
          pageSize: 50,
          sort_attribute: 'created_at',
          sort_direction: 'DESC',
        },
      },
      onSuccess: (data) => {
        const scenario = defaultDcaScenario(data.results);
        if (scenario) setSelectedDcaScenario(scenario);
      },
    });
  }, [fetchDcaScenarios, selectedDcaScenario, setSelectedDcaScenario]);
}
